import api from './api';

export interface SystemUser {
  id: number;
  username: string;
  password: string;
  employeeName: string;
  email?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  userRoles: {
    id: number;
    role: {
      id: number;
      name: string;
      description?: string;
    };
    project: {
      id: number;
      name: string;
    };
  }[];
}

export interface CreateSystemUserData {
  username: string;
  password: string;
  employeeName: string;
  email?: string;
  isActive?: boolean;
  projectRoles: {
    projectId: number;
    roleIds: number[];
  }[];
}

export interface UpdateSystemUserData extends CreateSystemUserData {
  id: number;
}

export interface SystemUserFilters {
  search?: string;
  isActive?: boolean;
  projectId?: number;
  roleId?: number;
  page?: number;
  limit?: number;
}

export interface SystemUsersResponse {
  users: SystemUser[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ProjectsAndRolesResponse {
  projects: {
    id: number;
    name: string;
    isActive: boolean;
  }[];
  roles: {
    id: number;
    name: string;
    description?: string;
  }[];
}

class SystemUserService {
  async getSystemUsers(filters: SystemUserFilters = {}): Promise<SystemUsersResponse> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters.projectId) params.append('projectId', filters.projectId.toString());
    if (filters.roleId) params.append('roleId', filters.roleId.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`/system-users?${params}`);
    return response.data;
  }

  async getSystemUserById(id: number): Promise<SystemUser> {
    const response = await api.get(`/system-users/${id}`);
    return response.data;
  }

  async createSystemUser(data: CreateSystemUserData): Promise<SystemUser> {
    const response = await api.post('/system-users', data);
    return response.data;
  }

  async updateSystemUser(id: number, data: CreateSystemUserData): Promise<SystemUser> {
    const response = await api.put(`/system-users/${id}`, data);
    return response.data;
  }

  async deleteSystemUser(id: number): Promise<void> {
    await api.delete(`/system-users/${id}`);
  }

  async getProjectsAndRoles(): Promise<ProjectsAndRolesResponse> {
    const response = await api.get('/system-users/data/projects-and-roles');
    return response.data;
  }

  // Utility functions
  formatUserName(user: SystemUser): string {
    return `${user.employeeName} (${user.username})`;
  }

  getUserProjectCount(user: SystemUser): number {
    const uniqueProjects = new Set(user.userRoles.map(ur => ur.project.id));
    return uniqueProjects.size;
  }

  getUserRoleCount(user: SystemUser): number {
    return user.userRoles?.length || 0;
  }

  getUserProjectRoles(user: SystemUser): Record<string, string[]> {
    const projectRoles: Record<string, string[]> = {};
    
    user.userRoles.forEach(userRole => {
      const projectName = userRole.project.name;
      const roleName = userRole.role.name;
      
      if (!projectRoles[projectName]) {
        projectRoles[projectName] = [];
      }
      
      if (!projectRoles[projectName].includes(roleName)) {
        projectRoles[projectName].push(roleName);
      }
    });
    
    return projectRoles;
  }

  validateUserData(data: CreateSystemUserData): string[] {
    const errors: string[] = [];

    if (!data.username?.trim()) {
      errors.push('Username is required');
    }

    if (data.username && data.username.trim().length < 3) {
      errors.push('Username must be at least 3 characters long');
    }

    if (data.username && data.username.trim().length > 30) {
      errors.push('Username cannot exceed 30 characters');
    }

    if (data.username && !/^[a-zA-Z0-9_]+$/.test(data.username.trim())) {
      errors.push('Username can only contain letters, numbers, and underscores');
    }

    if (!data.password?.trim()) {
      errors.push('Password is required');
    }

    if (data.password && data.password.length < 6) {
      errors.push('Password must be at least 6 characters long');
    }

    if (!data.employeeName?.trim()) {
      errors.push('Employee name is required');
    }

    if (data.employeeName && data.employeeName.trim().length < 2) {
      errors.push('Employee name must be at least 2 characters long');
    }

    if (data.email && data.email.trim() && !/\S+@\S+\.\S+/.test(data.email.trim())) {
      errors.push('Please enter a valid email address');
    }

    if (!data.projectRoles || data.projectRoles.length === 0) {
      errors.push('At least one project and role must be assigned');
    }

    data.projectRoles?.forEach((projectRole, index) => {
      if (!projectRole.projectId) {
        errors.push(`Project is required for assignment ${index + 1}`);
      }
      if (!projectRole.roleIds || projectRole.roleIds.length === 0) {
        errors.push(`At least one role is required for assignment ${index + 1}`);
      }
    });

    return errors;
  }

  formatUserStatus(isActive: boolean): string {
    return isActive ? 'Active' : 'Inactive';
  }

  getStatusColor(isActive: boolean): string {
    return isActive ? '#10b981' : '#6b7280';
  }
}

export const systemUserService = new SystemUserService();
